import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { map } from 'lodash';
import { useMemberListContext } from '@frontend/app/context/MemberListContext';
import { useMemberSearchQuery } from '@frontend/app/hooks';
import { useMessagingContext } from '@frontend/hooks';

import { IMemberSearchQuery } from '@frontend/app/types/MemberSearch';
import { IColumn, IColumnVisibility } from '@frontend/app/types/Columns';
import { MemberSearchQuery_members as IMember } from '@frontend/app/queries/types/MemberSearchQuery';
import { ITableColumnConfig } from '@components';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shadcn/components/ui/tooltip';
import { Button as ShadcnButton } from '@/shadcn/components/ui/button';
import DataTable from '@/app/refresh-components/DataTable';

import { useMemberSearchQueryVariables } from './hooks';
import { defaultRenderCell } from './utils/defaultRenderCell';

const { useMemo: ReactUseMemo } = React;

// Safe wrapper for ProspectsListToolbar that handles context availability
const SafeProspectsListToolbar = React.lazy(() =>
  import('@/app/containers/Projects/ProjectsPage/ProspectsList/ProspectsListToolbar').then((module) => ({
    default: () => {
      try {
        return <module.ProspectsListToolbar />;
      } catch (error) {
        console.warn('MemberListContext not available for toolbar:', error);
        return null;
      }
    }
  }))
);

interface IMemberTableFlexProps {
  columns: IColumn[];
  columnVisibility: IColumnVisibility;
  searchQuery: IMemberSearchQuery;
  showCheckboxColumn?: boolean;
  showMembersChangedNotice?: boolean;
  headerActions?: (refetchData?: () => void) => React.ReactElement;
  tableHeader?: React.ReactElement;
  membersHaveChanged: boolean;
  onSelectedMembersChange?: (selectedMembers: IMember[]) => void;
  onSortDirChange?: (columnField: string, sortDir: string) => void;
  onRefreshClick: () => void;
  onDismissMembersHaveChangedNotice: () => void;
  renderCell?: (column: IColumn) => ITableColumnConfig;
  refetchProjectCounts?: () => Promise<any>;
  refetchData: () => Promise<void>;
}

// Helper component for cell content with ellipsis and tooltip
const CellWithTooltip: React.FC<{ content: React.ReactNode; isText?: boolean }> = ({ content, isText = false }) => {
  const [isOverflowing, setIsOverflowing] = React.useState(false);
  const textRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const element = textRef.current;
    if (element && isText) {
      setIsOverflowing(element.scrollWidth > element.clientWidth);
    }
  }, [content, isText]);

  if (!isText || !isOverflowing) {
    return (
      <div ref={textRef} className="truncate">
        {content}
      </div>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div ref={textRef} className="truncate cursor-help">
            {content}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{content}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const MemberTableFlex: React.FC<IMemberTableFlexProps> = ({
  columns,
  columnVisibility,
  searchQuery,
  showCheckboxColumn = false,
  showMembersChangedNotice = false,
  headerActions,
  tableHeader,
  membersHaveChanged,
  onSelectedMembersChange,
  onSortDirChange,
  onRefreshClick,
  onDismissMembersHaveChangedNotice,
  renderCell,
  refetchProjectCounts,
  refetchData,
}) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const { showError } = useMessagingContext();

  // Get context data for pagination and pageSize
  const { page, pageSize, updateCount } = useMemberListContext();

  const baseMemberSearchQueryVariables = useMemberSearchQueryVariables(
    searchQuery,
    columnVisibility,
    pageSize,
  );

  // For flex UI, we need a separate query that handles pagination at the query level
  const flexUIQueryVariables = useMemo(() => {
    const currentPage = page !== undefined ? page : 0;
    return {
      ...baseMemberSearchQueryVariables,
      skip: (currentPage - 1) * pageSize,
      take: pageSize,
    };
  }, [baseMemberSearchQueryVariables, pageSize, page]);

  const skipLoadingMembers = useMemo(() => !searchQuery || !columnVisibility, [
    searchQuery,
    columnVisibility,
  ]);

  const {
    error: flexError,
    data: { members: flexMembersList = null } = {},
    refetch: refetchFlexMembers,
  } = useMemberSearchQuery({
    variables: flexUIQueryVariables,
    skip: skipLoadingMembers,
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (flexMembersList) {
      updateCount(flexMembersList.length);
    }
  }, [flexMembersList, updateCount]);

  // Handle flex UI errors
  useEffect(() => {
    if (flexError) {
      showError(flexError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flexError]);

  const dataTableColumns = ReactUseMemo<ColumnDef<any>[]>(() => {
    return columns.map((column): ColumnDef<any> => ({
      id: column.field,
      accessorKey: column.field,
      header: () => (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">{column.headerName}</span>
        </div>
      ),
      cell: ({ row, column: tableColumn }) => {
        const columnConfig = renderCell
          ? renderCell(column)
          : defaultRenderCell(column, { refetchProjectCounts });

        if (columnConfig.render) {
          const result = columnConfig.render(row.original[tableColumn.id], {
            isEditing: false,
            isRowSelected: false,
            isCellSelected: false,
            onToggleEditing: () => {},
            onChangeCellValue: () => {},
            onToggleRowSelected: () => {},
            onToggleShift: () => {},
          });
          return result !== undefined && result !== null ? (
            <CellWithTooltip content={result} isText={typeof result === 'string'} />
          ) : null;
        }

        const cellValue = row.original[tableColumn.id];
        const displayValue = cellValue !== undefined && cellValue !== null ? String(cellValue) : null;

        return displayValue ? (
          <CellWithTooltip content={displayValue} isText />
        ) : null;
      },
      enableSorting: true,
      size: column.field === 'name' ? 350 : 250,
    }));
  }, [columns, renderCell, refetchProjectCounts]);

  const handleDataTableSelectionChange = useCallback(
    (selectedRows: any[]) => {
      const selectedMemberObjects = selectedRows.map((row) => row._raw);
      if (onSelectedMembersChange) {
        onSelectedMembersChange(selectedMemberObjects);
      }
    },
    [onSelectedMembersChange],
  );

  const handleSortingChange = useCallback(
    (updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => {
      const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
      setSorting(newSorting);

      if (onSortDirChange && newSorting.length > 0) {
        const sort = newSorting[0];
        onSortDirChange(sort.id, sort.desc ? 'desc' : 'asc');
      }
    },
    [sorting, onSortDirChange],
  );

  // Update refetch data to use flex refetch
  const handleRefetchData = useCallback(async () => {
    await refetchFlexMembers();
    await refetchData();
  }, [refetchFlexMembers, refetchData]);

  // Transform members data for table
  const tableData = useMemo(() => {
    if (!flexMembersList) return [];

    return map(flexMembersList, (member) => ({
      ...member,
      name: {
        id: member.id,
        name: member.name,
        profile: member.profilePicture,
      },
      id: String(member.id),
      _raw: member,
    }));
  }, [flexMembersList]);

  return (
    <div className="space-y-4">
      {showMembersChangedNotice && membersHaveChanged && (
        <div className="rounded-lg border border-border bg-blue-1 p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-foreground">
              Member data are updated. Click Refresh to see the updated data
            </span>
            <div className="flex gap-2">
              <ShadcnButton
                variant="default"
                size="sm"
                onClick={onRefreshClick}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Refresh
              </ShadcnButton>
              <ShadcnButton
                variant="outline"
                size="sm"
                onClick={onDismissMembersHaveChangedNotice}
              >
                Dismiss
              </ShadcnButton>
            </div>
          </div>
        </div>
      )}

      {tableHeader}

      <div className="flex items-center justify-between px-6">
        {headerActions(handleRefetchData)}
        <React.Suspense fallback={null}>
          <SafeProspectsListToolbar />
        </React.Suspense>
      </div>

      <DataTable
        columns={dataTableColumns}
        data={tableData}
        sortable
        selectable={showCheckboxColumn}
        onSortingChange={handleSortingChange}
        sorting={sorting}
        draggable={false}
        maxHeight="calc(100vh - 240px)"
        bordered
        wrapperClassName="rounded-lg border border-border bg-card"
        tableContentClassName="bg-background"
        clearSelection={false}
        onSelectionChange={handleDataTableSelectionChange}
      />
    </div>
  );
};
